import { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";

import { api_without_cancellation } from "../../../api/api";
import { formatDateAtTime, getCaseId, getClientId } from "../../../Utils/helper";

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmt = (s) => {
  if (!isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};

const normalizeTranscriptSegments = (rawTranscript) => {
  if (Array.isArray(rawTranscript)) {
    return rawTranscript.filter((segment) => segment?.text?.trim());
  }
  if (typeof rawTranscript === "string" && rawTranscript.trim()) {
    return [{ text: rawTranscript.trim(), speaker_label: "Transcript" }];
  }
  return [];
};

const formatTranscriptTimestamp = (seconds) => {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) return "";
  return fmt(Number(seconds));
};

// ── Icons ─────────────────────────────────────────────────────────────────────
const IconPlay = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <path d="M8 5v14l11-7z" />
  </svg>
);
const IconPause = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
  </svg>
);
const IconRestart = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" />
  </svg>
);
const IconRewind = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M11 18V6l-8.5 6 8.5 6zm.5-6l8.5 6V6l-8.5 6z" />
  </svg>
);
const IconFastForward = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
    <path d="M4 18l8.5-6L4 6v12zm9-12v12l8.5-6L13 6z" />
  </svg>
);

// ── Modal ─────────────────────────────────────────────────────────────────────
const RecordingPlayerModal = ({ show, onClose, callData, clientData, onDescriptionSaved }) => {
  const audioRef = useRef(null);
  const intervalRef = useRef(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [transcriptSegments, setTranscriptSegments] = useState([]);
  const [description, setDescription] = useState("");
  const [savingDescription, setSavingDescription] = useState(false);
  const [descriptionError, setDescriptionError] = useState(null);

  useEffect(() => {
    setDescription(callData?.description || "");
    setDescriptionError(null);
  }, [callData]);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!show || !callData?.call_id) return;

    let objectUrl = null;

    const load = async () => {
      setLoading(true);
      setError(null);
      setPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setAudioUrl(null);

      try {
        const response = await api_without_cancellation.get(
          `/api/client-page/client-communication/calls/${callData.call_id}/recording/`,
          { responseType: "arraybuffer" }
        );
        const blob = new Blob([response.data], { type: "audio/wav" });
        objectUrl = URL.createObjectURL(blob);
        setAudioUrl(objectUrl);
      } catch (err) {
        setError("Failed to load recording.");
      } finally {
        setLoading(false);
      }
    };

    load();

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [show, callData?.call_id]);

  useEffect(() => {
    if (!show || !callData?.call_id) return;

    let active = true;
    setTranscriptSegments([]);

    const loadTranscript = async () => {
      try {
        const response = await api_without_cancellation.get(
          `/api/client-page/client-communication/calls/${callData.call_id}/`
        );
        if (active) {
          setTranscriptSegments(normalizeTranscriptSegments(response?.data?.transcript_segments));
        }
      } catch (err) {
        if (active) setTranscriptSegments([]);
      }
    };

    loadTranscript();
    return () => {
      active = false;
    };
  }, [show, callData?.call_id]);

  // ── Wire audio element ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!audioUrl) return;

    const audio = new Audio(audioUrl);
    audio.volume = volume;
    audio.playbackRate = playbackRate;
    audioRef.current = audio;

    const onLoaded = () => setDuration(audio.duration);
    const onEnded = () => {
      setPlaying(false);
      setCurrentTime(audio.duration);
      clearInterval(intervalRef.current);
    };
    const onError = () => setError("Playback error.");

    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("error", onError);

    return () => {
      audio.pause();
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("error", onError);
      clearInterval(intervalRef.current);
    };
  }, [audioUrl]);

  // ── Interval ticker — polls every 250ms so slider moves smoothly ───────────
  useEffect(() => {
    if (playing) {
      intervalRef.current = setInterval(() => {
        if (audioRef.current) {
          setCurrentTime(audioRef.current.currentTime);
        }
      }, 250);
    } else {
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [playing]);

  // ── Controls ───────────────────────────────────────────────────────────────
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play();
      setPlaying(true);
    }
  };

  const restart = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = 0;
    setCurrentTime(0);
    audio.play();
    setPlaying(true);
  };

  const skip = useCallback(
    (secs) => {
      const audio = audioRef.current;
      if (!audio) return;
      const next = Math.min(Math.max(0, audio.currentTime + secs), duration);
      audio.currentTime = next;
      setCurrentTime(next);
    },
    [duration]
  );

  const handleSeekChange = (e) => {
    const val = parseFloat(e.target.value);
    setCurrentTime(val);
    if (audioRef.current) audioRef.current.currentTime = val;
  };

  const handleRateChange = (rate) => {
    setPlaybackRate(rate);
    if (audioRef.current) audioRef.current.playbackRate = rate;
  };

  const handleDownload = () => {
    if (!audioUrl) return;
    const a = document.createElement("a");
    a.href = audioUrl;
    a.download = `recording-call-${callData?.call_id ?? "export"}.wav`;
    a.click();
  };

  const handleTranscriptDownload = () => {
    if (!transcriptSegments.length) return;

    const transcript = transcriptSegments
      .map((segment, index) => {
        const speaker = segment.display_name || segment.speaker_label || `Speaker ${index + 1}`;
        const timestamp = formatTranscriptTimestamp(segment.start_time);
        return `${speaker}${timestamp ? ` [${timestamp}]` : ""}:\n${segment.text.trim()}`;
      })
      .join("\n\n");
    const blob = new Blob([transcript], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `transcript-call-${callData?.call_id ?? "export"}.txt`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const handleSaveDescription = async () => {
    if (!callData?.id || savingDescription) return;

    try {
      setSavingDescription(true);
      setDescriptionError(null);
      await api_without_cancellation.patch(
        `/api/recordings/${getClientId()}/${getCaseId()}/${callData.id}/`,
        { description }
      );
      await onDescriptionSaved?.();
      onClose();
    } catch (err) {
      setDescriptionError(
        err.response?.data?.description?.[0] ||
          err.response?.data?.message ||
          err.response?.data?.detail ||
          "Failed to update description."
      );
    } finally {
      setSavingDescription(false);
    }
  };

  const handleClose = () => {
    if (savingDescription) return;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    clearInterval(intervalRef.current);
    setPlaying(false);
    setAudioUrl(null);
    setCurrentTime(0);
    setDuration(0);
    setError(null);
    setTranscriptSegments([]);
    onClose();
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const rates = [0.5, 1, 1.5, 2];
  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return "N/A";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    if (m === 0) return `${s} sec${s !== 1 ? "s" : ""}`;
    if (s === 0) return `${m} min${m !== 1 ? "s" : ""}`;
    return `${m} min${m !== 1 ? "s" : ""} ${s} sec${s !== 1 ? "s" : ""}`;
  };
  const formatPhoneNumber = (phone) => {
    const digits = String(phone).replace(/\D/g, "");
    const d = digits.slice(-10);
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  };
  const resolveStaffPerson2 = (data) =>
    data?.type === "InboundRecordedCall" ? data?.answered_by : data?.initiated_by;
  const buildDescription = (data, clientData) => {
    const staff = resolveStaffPerson2(data);
    const staffName = `${staff?.first_name || ""} ${staff?.last_name || ""}`.trim();
    const clientName =
      `${clientData?.client?.first_name || ""} ${clientData?.client?.last_name || ""}`.trim();
    const isInbound = data?.type === "InboundRecordedCall";

    const from = isInbound ? clientName : staffName;
    const to = isInbound ? staffName : clientName;

    return `Conversation initiated by ${from} to ${to} at ${formatPhoneNumber(data?.client_phone_number)} beginning on ${formatDateAtTime(data?.created_at)} lasting ${formatDuration(data?.duration_seconds)}`.trim();
  };
  // const buildDescription = (data, clientData) =>
  //   `Conversation initiated by ${data?.initiated_by?.first_name || ""} ${data?.initiated_by?.last_name || ""} to ${clientData?.client?.first_name || ""} ${clientData?.client?.last_name || ""} at ${formatPhoneNumber(data?.client_phone_number)} beginning on ${formatDateAtTime(data?.created_at)} lasting ${formatDuration(data?.duration_seconds)}`.trim();
  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="md"
      dialogClassName="modal-dialog modal-dialog-centered"
      contentClassName="custom-modal-new-provider"
    >
      {/* ── Header ── */}
      <Modal.Header
        style={{
          borderBottom: "0px",
        }}
        className="text-center height-25 p-0 bg-primary popup-heading-color justify-content-center"
      >
        <Modal.Title
          className="mx-auto height-25 font-weight-semibold text-uppercase popup-heading-color d-flex align-items-center"
          id="modal_title"
          style={{ fontSize: "14px", fontWeight: "600" }}
        >
          Call Recording
        </Modal.Title>
      </Modal.Header>

      {/* ── Body ── */}
      <Modal.Body style={{ padding: "5px", minHeight: "145px" }}>
        {/* Loading */}
        {loading && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              minHeight: 120,
            }}
          >
            <span className="rp-spinner" />
            <span style={{ fontSize: 13, color: "#1a4f8a", fontWeight: 600 }}>
              Loading recording…
            </span>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div
            style={{
              background: "#fff5f5",
              border: "1px solid #f5c6cb",
              borderRadius: 6,
              padding: "12px 14px",
            }}
          >
            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#c0392b" }}>{error}</p>
          </div>
        )}

        {/* Player */}
        {!loading && !error && audioUrl && (
          <>
            {" "}
            <div
              className="d-flex align-items-center"
              style={{ gap: "5px", fontWeight: "600", padding: "5px" }}
            >
              {buildDescription(callData, clientData)}
            </div>
            <div className="rp-player">
              {/* Seek bar */}
              <div className="rp-seek-row">
                <span className="rp-time-lbl text-end">{fmt(currentTime)}</span>
                <div className="rp-track-wrap">
                  <div className="rp-track-bg" />
                  <div className="rp-track-fill" style={{ width: `${progress}%` }} />
                  <div className="rp-thumb" style={{ left: `${progress}%` }} />
                  <input
                    type="range"
                    className="rp-range"
                    min={0}
                    max={duration || 0}
                    step={0.1}
                    value={currentTime}
                    onChange={handleSeekChange}
                  />
                </div>
                <span className="rp-time-lbl text-start">{fmt(duration)}</span>
              </div>

              {/* Controls */}
              <div className="rp-controls">
                <button className="rp-ctrl" onClick={restart} title="Restart">
                  <IconRestart />
                </button>

                <button
                  className="rp-ctrl rp-ctrl--skip"
                  onClick={() => skip(-10)}
                  title="Rewind 10s"
                >
                  <IconRewind />
                  <span className="rp-skip-num">10</span>
                </button>

                <button className="rp-play-btn" onClick={togglePlay}>
                  {playing ? <IconPause /> : <IconPlay />}
                </button>

                <button
                  className="rp-ctrl rp-ctrl--skip"
                  onClick={() => skip(10)}
                  title="Forward 10s"
                >
                  <IconFastForward />
                  <span className="rp-skip-num">10</span>
                </button>

                <div className="rp-rates">
                  {rates.map((r) => (
                    <button
                      key={r}
                      className={`rp-rate ${playbackRate === r ? "rp-rate--on" : ""}`}
                      onClick={() => handleRateChange(r)}
                    >
                      {r}×
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}

        {descriptionError && (
          <div className="alert alert-danger py-2 px-3 mt-2 mb-0" role="alert">
            {descriptionError}
          </div>
        )}

        {callData && (
          <div className="mt-2">
            <label htmlFor="call-recording-description" className="form-label mb-1 fw-semibold">
              Description
            </label>
            <textarea
              id="call-recording-description"
              className="form-control"
              rows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              disabled={savingDescription}
              placeholder="Add a description for this recording"
            />
          </div>
        )}
      </Modal.Body>

      {/* ── Footer ── */}
      <Modal.Footer
        className="p-0 mt-0 padding-outside-btn-new-provider d-flex align-items-center justify-content-center"
        style={{ borderTop: "none" }}
      >
        <Button
          variant="secondary"
          className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
          onClick={handleClose}
          disabled={savingDescription}
          style={{ fontSize: 14, fontWeight: 600 }}
        >
          Close
        </Button>
        <Button
          variant="danger"
          className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
          onClick={() => {}}
          disabled={savingDescription}
          style={{ fontSize: 14, fontWeight: 600 }}
        >
          Delete
        </Button>
        <Button
          variant="primary"
          className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
          onClick={handleSaveDescription}
          disabled={!callData?.id || savingDescription}
          style={{ fontSize: 14, fontWeight: 600 }}
        >
          {savingDescription ? "Saving..." : "Save"}
        </Button>
        <div className="d-flex align-items-center gap-1">
          <Button
            variant="primary"
            className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
            onClick={handleDownload}
            disabled={!audioUrl || loading || savingDescription}
            style={{ fontSize: 14, fontWeight: 600 }}
          >
            Download
          </Button>
          {transcriptSegments.length > 0 && (
            <Button
              variant="primary"
              className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
              onClick={handleTranscriptDownload}
              disabled={savingDescription}
              style={{ fontSize: 14, fontWeight: 600, width: "max-content", padding: "0 10px" }}
            >
              Download Transcript
            </Button>
          )}
        </div>
      </Modal.Footer>

      <style>{`
        .rp-spinner {
          display: inline-block;
          width: 16px; height: 16px;
          border: 2px solid #b8d4f5;
          border-top-color: #1a4f8a;
          border-radius: 50%;
          animation: rp-spin 0.75s linear infinite;
          flex-shrink: 0;
        }
        @keyframes rp-spin { to { transform: rotate(360deg); } }

        .rp-player {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        /* ── Track (shared by seek + volume) ── */
        .rp-seek-row {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .rp-time-lbl {
          font-size: 12px;
          font-weight: 600;
          color: #4a5568;
          min-width: 36px;
          white-space: nowrap;
          font-variant-numeric: tabular-nums;
        }
        .rp-track-wrap {
          flex: 1;
          position: relative;
          height: 20px;
          display: flex;
          align-items: center;
          cursor: pointer;
        }
        .rp-track-bg,
        .rp-track-fill {
          position: absolute;
          left: 0;
          height: 5px;
          border-radius: 4px;
          pointer-events: none;
        }
        .rp-track-bg   { width: 100%; background: #dde3ea; }
        .rp-track-fill { background: #1a4f8a; }
        .rp-thumb {
          position: absolute;
          width: 14px; height: 14px;
          background: #1a4f8a;
          border: 2px solid #fff;
          border-radius: 50%;
          box-shadow: 0 1px 4px rgba(0,0,0,0.22);
          pointer-events: none;
          transform: translateX(-50%);
        }
        .rp-range {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          cursor: pointer;
          margin: 0; padding: 0;
        }
        .rp-track-wrap:hover .rp-track-fill,
        .rp-track-wrap:hover .rp-thumb { background: #154080; }

        /* ── Controls ── */
        .rp-controls {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .rp-ctrl {
          background: #f0f4f8;
          border: 1px solid #d5dde8;
          border-radius: 50%;
          width: 34px; height: 34px;
          display: flex; align-items: center; justify-content: center;
          flex-direction: column;
          color: #1a4f8a;
          cursor: pointer;
          padding: 0;
          gap: 1px;
          transition: background 0.15s;
          flex-shrink: 0;
        }
        .rp-ctrl:hover { background: #dce6f0; }
        .rp-ctrl--skip { flex-direction: column; }
        .rp-skip-num {
          font-size: 8px;
          font-weight: 700;
          color: #1a4f8a;
          line-height: 1;
        }
        .rp-play-btn {
          background: #1a4f8a;
          border: none;
          border-radius: 50%;
          width: 46px; height: 46px;
          display: flex; align-items: center; justify-content: center;
          color: #fff;
          cursor: pointer;
          flex-shrink: 0;
          box-shadow: 0 3px 10px rgba(26,79,138,0.35);
          transition: background 0.15s, transform 0.1s;
        }
        .rp-play-btn:hover  { background: #154080; transform: scale(1.05); }
        .rp-play-btn:active { transform: scale(0.97); }

        /* Speed */
        .rp-rates {
          display: flex;
          gap: 3px;
          background: #f0f4f8;
          border: 1px solid #d5dde8;
          border-radius: 20px;
          padding: 3px;
        }
        .rp-rate {
          background: transparent;
          border: none;
          border-radius: 14px;
          padding: 2px 7px;
          font-size: 11px;
          font-weight: 700;
          color: #6b7f95;
          cursor: pointer;
          transition: background 0.15s, color 0.15s;
          white-space: nowrap;
        }
        .rp-rate:hover { color: black; }
        .rp-rate--on   { background: #1a4f8a; color: #fff; }

        /* Volume */
        .rp-vol-row {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #6b7f95;
        }
        .rp-vol-track { max-width: 150px; }
        .rp-vol-pct {
          font-size: 11px;
          font-weight: 600;
          color: #6b7f95;
          min-width: 34px;
          text-align: right;
        }

        @media (max-width: 480px) {
          .rp-controls { gap: 6px; }
          .rp-play-btn { width: 40px; height: 40px; }
          .rp-ctrl     { width: 30px; height: 30px; }
          .rp-rate     { padding: 2px 5px; font-size: 10px; }
        }
      `}</style>
    </Modal>
  );
};

export default RecordingPlayerModal;
